<template>
  <!-- RQ System Summary card: GET orders/summary/show (G-80, the pro panel "Summary" tab body + can_edit_notes) in the app
       theme. Used by the System Summary page and Submit PerfectMatch's first step; loads itself, emits "failed" with the
       server's message. Slot "band" = actions on the purple company band (the page's Edit Summary). -->
  <div v-if="loading" class="flex flex-col gap-[20px]">
    <div v-for="n in 3" :key="n" class="fxShimmer h-[160px] rounded-[20px]" />
  </div>
  <div v-else-if="view" class="rqCard !p-0 overflow-hidden">
    <!-- company band -->
    <div class="flex flex-wrap items-center justify-between gap-[12px] px-[15px] laptop:px-[20px] py-[12px] bg-[#8479E1]">
      <div class="flex items-center gap-[14px] min-w-0">
        <span v-if="view.companyDetail.logo" class="flex-shrink-0 px-[8px] py-[4px] rounded-[6px] bg-white">
          <img :src="view.companyDetail.logo" :alt="view.companyDetail.name" class="h-[36px] max-w-[120px] object-contain">
        </span>
        <span v-if="view.companyDetail.logo" class="hidden tablet:block w-[2px] h-[36px] bg-[rgba(255,255,255,0.5)]" />
        <div class="min-w-0 text-white">
          <p class="m-0 text-[16px] font-medium break-words">
{{ view.companyDetail.name }}
</p>
          <p class="m-0 text-[13px] opacity-[0.9]">
License No: {{ view.companyDetail.license }}
</p>
        </div>
      </div>
      <slot name="band" />
    </div>

    <div class="px-[15px] laptop:px-[20px] py-[18px]">
      <p class="m-0 pb-[16px] border-b-1 border-[#EFEFEF] text-[16px] laptop:text-[18px] font-semibold text-[#8479E1]">
{{ view.heading }}
</p>
      <p v-if="view.title" class="m-0 mt-[16px] text-[16px] laptop:text-[18px] font-semibold text-[#333333] break-words">
{{ view.title }}
</p>

      <!-- line items by group, then the additional items -->
      <template v-for="group in view.items" :key="group.key">
        <p class="m-0 pt-[16px] pb-[10px] text-[15px] font-medium text-[#F67280]">
{{ group.heading }}
</p>
        <div class="flex flex-col gap-[6px]">
          <div
v-for="(row, i) in group.data" :key="i"
            class="flex items-center justify-between gap-[12px] px-[12px] py-[10px] rounded-[8px] border-1 border-[#EFEFEF] bg-[#F9F9FB]"
>
            <span class="min-w-0 text-[14px] text-[#333333] break-words">{{ row.option }}</span>
            <span class="flex-shrink-0 text-[14px] font-semibold text-[#00B7C2]">{{ row.total_cost_text }}</span>
          </div>
        </div>
      </template>
      <template v-if="view.additional_items.length">
        <p class="m-0 pt-[16px] pb-[10px] text-[15px] font-medium text-[#F67280]">
Additional Items
</p>
        <div class="flex flex-col gap-[6px]">
          <div
v-for="(row, i) in view.additional_items" :key="'ai' + i"
            class="flex items-center justify-between gap-[12px] px-[12px] py-[10px] rounded-[8px] border-1 border-[#EFEFEF] bg-[#F9F9FB]"
>
            <span class="min-w-0 text-[14px] text-[#333333] break-words">{{ row.title }}</span>
            <span class="flex-shrink-0 text-[14px] font-semibold text-[#00B7C2]">{{ row.value }}</span>
          </div>
        </div>
      </template>

      <!-- notes: an editor for the quote's sales person, read-only for everyone else (like the web view) -->
      <template v-if="view.can_edit_notes">
        <label class="block m-0 pt-[16px] pb-[10px] text-[15px] font-medium text-[#F67280]" for="rqSummaryNotes">Notes</label>
        <textarea
id="rqSummaryNotes" v-model="notesDraft" rows="4" placeholder="Enter notes here"
          class="w-full px-[12px] py-[10px] rounded-[8px] border-1 border-[#E1E5EF] text-[14px] text-[#333333] outline-none focus:border-[#8479E1] resize-y"
/>
        <div class="flex justify-end mt-[8px]">
          <button type="button" class="uqcBtnNext" :disabled="savingNotes || notesDraft === (view.notes || '')" @click="saveNotes">
Save Notes
</button>
        </div>
      </template>
      <template v-else-if="view.notes">
        <p class="m-0 pt-[16px] pb-[10px] text-[15px] font-medium text-[#F67280]">
Notes
</p>
        <p class="m-0 px-[12px] py-[10px] rounded-[8px] border-1 border-[#EFEFEF] text-[14px] text-[#333333] whitespace-pre-line break-words">
{{ view.notes }}
</p>
      </template>

      <!-- totals, like the web view: adjustment, Sub Total, each discount, Total -->
      <div class="flex flex-col gap-[6px] mt-[16px]">
        <div v-if="view.adjustment" class="flex justify-between gap-[12px] px-[12px] text-[14px] text-[#333333]">
          <span>{{ view.adjustment.title }}</span><span>{{ view.adjustment.amount }}</span>
        </div>
        <div class="flex justify-between gap-[12px] px-[12px] text-[14px] text-[#333333]">
          <span>Sub Total</span><span>{{ money(view.total.sub_total) }}</span>
        </div>
        <div v-for="(d, i) in view.discounts" :key="'d' + i" class="flex justify-between gap-[12px] px-[12px] text-[14px] text-[#333333]">
          <span>{{ d.title }}</span><span>- {{ money(d.amount) }}</span>
        </div>
        <div class="flex items-center justify-between gap-[12px] px-[12px] py-[12px] rounded-[8px] bg-[#EEF2FF]">
          <span class="text-[15px] font-medium text-[#333333]">Total due upon installation</span>
          <span class="text-[17px] font-bold text-[#333333]">{{ money(view.total.total) }}</span>
        </div>
      </div>

      <p class="m-0 mt-[12px] text-[14px] text-[#F67280]">
{{ view.payment_note }}
</p>
      <p v-if="view.support_phone" class="m-0 mt-[4px] text-[13px] text-[#666666]">
Talk to a system specialist
        <a :href="'tel:' + view.support_phone" class="text-[#7695FF] no-underline">{{ view.support_phone }}</a>
      </p>

      <div v-if="view.signature" class="mt-[15px]">
        <span class="rqLabel">Signature</span>
        <img :src="view.signature" alt="Signature" class="mt-[4px] max-w-[300px] h-[110px] object-contain border-b-1 border-[#999999]">
      </div>

      <!-- disclosure, open by default like the panel -->
      <div v-if="view.disclosure" class="mt-[18px] rounded-[10px] border-1 border-[#EFEFEF] overflow-hidden">
        <button
type="button"
          class="w-full flex items-center justify-between px-[12px] py-[10px] border-0 bg-[#8479E1] text-[14px] font-medium text-white"
          :aria-expanded="showDisclosure" @click="showDisclosure = !showDisclosure"
>
          Disclosure
          <svg
width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"
            :class="['transition-transform', !showDisclosure && 'rotate-180']"
>
            <path d="M6 15l6-6 6 6" stroke="#FFFFFF" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <p v-if="showDisclosure" class="m-0 px-[12px] py-[12px] text-[13px] leading-[1.6] text-[#333333] whitespace-pre-line">
{{ view.disclosure }}
</p>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import { rqApi } from "@/services/rqApi";
const toast = useToast();

export default {
  name: "RqSummaryCard",
  props: {
    orderId: { type: [String, Number], required: true },
  },
  emits: ["failed"],
  data() {
    return {
      // GET orders/summary/show body (the panel SummaryController shape)
      view: null,
      notesDraft: "",
      savingNotes: false,
      loading: true,
      showDisclosure: true,
    };
  },
  watch: {
    orderId: { immediate: true, handler() { this.load(); } },
  },
  methods: {
    async load() {
      this.loading = true;
      this.view = null;
      const res = await rqApi.summaryShow(this.orderId);
      if (res.ok && res.body) {
        this.view = res.body;
        this.notesDraft = res.body.notes || "";
      } else {
        this.$emit("failed", res.message || "Unable to load the system summary.");
      }
      this.loading = false;
    },
    async saveNotes() {
      this.savingNotes = true;
      const res = await rqApi.summaryNotes(this.orderId, this.notesDraft);
      this.savingNotes = false;
      if (!res.ok) {
        toast.error(res.message || "Unable to save the notes.");
        return;
      }
      this.view = { ...this.view, notes: this.notesDraft };
      toast.success(res.message || "Notes have been saved successfully.");
    },
    money(n) {
      return (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
    },
  },
};
</script>
